import type { Dataset } from '@effektify/types/definitions'
import type { Integration, IntegrationStatus } from 'src/api/responses'

type ConnectionStatusKey = 'active' | 'pending' | 'lost'

interface ConnectionStatus {
  key: ConnectionStatusKey
  label: string
}

interface HeroConnection {
  id: string
  name: string
  status: ConnectionStatusKey
}

type HeroState = 'loading' | 'no-connections' | 'no-data' | 'normal'

const CONNECTION_STATUSES: Record<IntegrationStatus, ConnectionStatus> = {
  active: { key: 'active', label: 'Active' },
  pending_authentication: { key: 'pending', label: 'Awaiting completion' },
  forbidden: { key: 'lost', label: 'Connection lost' },
}

const CONNECTION_STATUS_LABELS: Record<ConnectionStatusKey, string> = {
  active: CONNECTION_STATUSES.active.label,
  pending: CONNECTION_STATUSES.pending_authentication.label,
  lost: CONNECTION_STATUSES.forbidden.label,
}

const getConnectionStatus = (status: unknown): ConnectionStatus | null => (
  typeof status === 'string' && status in CONNECTION_STATUSES
    ? CONNECTION_STATUSES[status as IntegrationStatus]
    : null
)

const humanizeAdapterName = (adapter: string) => adapter
  .split(/[_-]/)
  .filter(Boolean)
  .map(word => word.charAt(0).toUpperCase() + word.slice(1))
  .join(' ')

const toHeroConnections = (integrations: Record<string, Integration[]> | undefined): HeroConnection[] => Object
  .values(integrations ?? {})
  .flat()
  .reduce<HeroConnection[]>((acc, integration) => {
    const status = getConnectionStatus(integration.status)
    if (!status) {
      return acc
    }
    acc.push({
      id: integration.id,
      name: integration.display_name || humanizeAdapterName(integration.adapter),
      status: status.key,
    })
    return acc
  }, [])

/** Numbers and numeric strings count as values; null, empty strings and NaN do not. */
const toNumber = (value: unknown): number | null => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null
  }
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : null
  }
  return null
}

/** A dataset is empty when it has no rows, or only rows without any values. */
const hasDatasetValues = (dataset: Dataset | null | undefined) => !!dataset?.data?.some(row => (
  Array.isArray(row) && row.some(cell => cell !== null && cell !== undefined && cell !== '')
))

const deriveHeroState = ({
  isLoading,
  connections,
  hasAnyData,
  isDataSettled,
}: {
  isLoading: boolean
  connections: HeroConnection[]
  hasAnyData: boolean
  isDataSettled: boolean
}): HeroState => {
  if (isLoading) {
    return 'loading'
  }
  if (connections.length === 0) {
    return 'no-connections'
  }
  if (hasAnyData) {
    return 'normal'
  }
  if (!isDataSettled) {
    return 'loading'
  }
  return 'no-data'
}

interface ChartMetric {
  id: string
  label: string
}

interface ChartSeriesInput<S> {
  seriesData: S[]
  series: { data: { key: string } }[]
}

const getSeriesKey = (key: string) => key.split('.').pop() ?? key

/** Each series in the chart card becomes a metric the user can switch between. */
const getChartMetrics = (seriesData: { id: string, label: string }[]): ChartMetric[] => seriesData
  .filter(Boolean)
  .map(({ id, label }) => ({ id, label }))

/**
 * Narrows a line chart to one series. Config and data are matched by data key, not
 * index, because series whose column is missing from the dataset are left out of the data.
 */
const selectChartSeries = <S extends { id: string, label: string, yAxisId?: string }>(
  { seriesData, series }: ChartSeriesInput<S>,
  metricId: string | undefined,
) => {
  const selected = seriesData.find(item => item?.id === metricId) ?? seriesData.find(Boolean)
  if (!selected) {
    return null
  }
  const { yAxisId: _yAxisId, ...singleAxisSeries } = selected
  const config = series.find(item => getSeriesKey(item.data.key) === selected.id)
  return {
    metric: { id: selected.id, label: selected.label },
    seriesData: [singleAxisSeries],
    series: [{ data: config?.data ?? { key: selected.id }, label: selected.label }],
  }
}

/** Replaces supported placeholders in report texts, such as `{year}` in the goal title. */
const fillTextPlaceholders = (text: string, now: Date = new Date()) =>
  text.replaceAll('{year}', String(now.getFullYear()))

/** How far from the benchmark still counts as on track, as a share of the benchmark. */
const BENCHMARK_ON_TRACK_MARGIN = 0.1
/** Below the benchmark by up to this share is "slightly" behind; further is behind. */
const BENCHMARK_SLIGHTLY_BEHIND_MARGIN = 0.25

/**
 * Compares the current value with the goal's benchmark, the value expected at this point
 * in the period. Returns null when there is no benchmark to compare with.
 */
const getProgressVerdict = ({
  value,
  benchmark,
  progress,
}: {
  value: unknown
  benchmark: number | null | undefined
  progress: number | null
}): string | null => {
  if (progress !== null && progress >= 100) {
    return 'Goal reached'
  }
  const current = toNumber(value)
  if (current === null || !benchmark || benchmark <= 0) {
    return null
  }
  const deviation = (current - benchmark) / benchmark
  if (deviation > BENCHMARK_ON_TRACK_MARGIN) return 'Ahead of plan'
  if (deviation >= -BENCHMARK_ON_TRACK_MARGIN) return 'On track'
  if (deviation >= -BENCHMARK_SLIGHTLY_BEHIND_MARGIN) return 'Slightly behind plan'
  return 'Behind plan'
}

const getGoalProgress = (data: { value?: unknown, type?: string } | undefined, goal: number | null | undefined): number | null => {
  const value = toNumber(data?.value)
  if (value === null) {
    return null
  }
  if (data?.type === 'percentage') {
    return value * 100
  }
  if (!goal || goal <= 0) {
    return null
  }
  return (value / goal) * 100
}

export {
  CONNECTION_STATUS_LABELS,
  getConnectionStatus,
  humanizeAdapterName,
  toHeroConnections,
  deriveHeroState,
  toNumber,
  hasDatasetValues,
  getChartMetrics,
  selectChartSeries,
  getProgressVerdict,
  getGoalProgress,
  fillTextPlaceholders,
}
export type { ChartMetric, ConnectionStatus, ConnectionStatusKey, HeroConnection, HeroState }
